import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogCTAButtons from "@/components/BlogCTAButtons";
import CanonicalTag from "@/components/CanonicalTag";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import heroImage from "@/assets/modern-adu-design-bay-area.png";

const BlogModernADUDesignBayArea = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Modern ADU Design in the Bay Area",
    "description": "Design that looks great and works long term. Learn what really matters when building a modern ADU in the Bay Area.",
    "image": heroImage,
    "datePublished": "2026-04-01",
    "author": { "@type": "Organization", "name": "UDA ADU" },
    "publisher": { "@type": "Organization", "name": "UDA ADU" },
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/blogs/modern-adu-design-bay-area" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Navigation />

      {/* Hero Image */}
      <div className="w-full max-w-5xl mx-auto px-4 pt-8">
        <img
          src={heroImage}
          alt="Modern ADU design at twilight in the Bay Area"
          className="w-full rounded-2xl"
        />
      </div>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blogs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>

        <p className="text-sm text-muted-foreground mb-2">April 1, 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
          Modern ADU Design in the Bay Area
        </h1>

        <article className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>
            When most people think about building an ADU in the Bay Area, they start with design. Clean lines, modern finishes, something that feels like a natural extension of the home.
          </p>
          <p>But design is only one piece of it.</p>
          <p>
            What matters more is whether the layout works. We see a lot of homeowners come in with inspiration photos, but once we look at the lot, zoning, setbacks, and utility access, the conversation changes. A good ADU is not just something that looks right. It needs to fit the property and the city requirements from day one.
          </p>
          <p>
            At UDA ADU, we take a design-build approach. That means we don't separate design from construction. We look at everything upfront so you don't run into surprises later.
          </p>
          <p>
            If you're planning to build an ADU in California, the first step is not picking finishes. It's understanding what your lot allows and what makes financial sense long term.
          </p>
        </article>

        <BlogCTAButtons />
      </main>

      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default BlogModernADUDesignBayArea;
